import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { STORAGE_USER_TOKEN_KEY } from '../types/user'
import SupportChat from './SupportChat'

const CHAT_ENDPOINT = '/api/assistant/chat'
const AVAILABILITY_ENDPOINT = '/api/assistant/availability'
const CONVERSATION_ENDPOINT = '/api/assistant/conversation'
const RESET_ENDPOINT = '/api/assistant/conversation/reset'
const EXPORT_ENDPOINT = '/api/assistant/export'

type ChatRole = 'user' | 'assistant' | 'error'

interface IChatMessage {
  id: number
  role: ChatRole
  text: string
  tools?: string[]
  exports?: IExport[]
}

// A reference to a file the server already built, not a link to one. The id
// identifies the artifact and nothing else — whose it is comes from the bearer
// token, so there is no URL to edit or forward. The filename is here only so
// the panel can label the button before the download starts.
interface IExport {
  id: number
  filename: string
}

type StreamFrame =
  | { type: 'status'; text: string }
  | { type: 'tool'; name: string; label: string }
  | {
      type: 'final'
      reply: string
      tools?: string[]
      followups?: string[]
      exports?: IExport[]
      mode?: AssistantMode
    }
  | { type: 'error'; message: string }

// Minimal markdown renderer. Covers only what the assistant is expected to
// send back: paragraphs, **bold**, bullet lists, and GFM tables. There is no
// markdown dependency in this app that also supports GFM tables out of the
// box (react-markdown is present, but remark-gfm is not), so rather than add
// a new dependency this renders the small subset by hand.
const isSeparatorRow = (line: string): boolean => {
  const cells = splitTableRow(line)
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell))
}

function splitTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return trimmed.split('|').map((cell) => cell.trim())
}

const isBulletLine = (line: string): boolean => /^\s*[-*]\s+/.test(line)

// Bold, links written as markdown, and links the model simply typed out. The
// help corpus carries walkthrough videos — several of them timestamped to a
// single step — so a reply that names one has to be clickable to be worth
// anything. Only http and https are matched, so a javascript: URL cannot be
// rendered as a link whatever the model emits; the server independently
// strips any URL absent from the documentation before it reaches here.
const INLINE_TOKEN =
  /(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s<>()[\]]+)/g
const MARKDOWN_LINK = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/
const BARE_URL = /^https?:\/\/[^\s<>()[\]]+$/
const TRAILING_PUNCTUATION = /[.,;:!?]+$/

const renderLink = (
  href: string,
  label: string,
  key: string,
): React.ReactNode => (
  <a href={href} key={key} rel="noopener noreferrer" target="_blank">
    {label}
  </a>
)

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] =>
  text
    .split(INLINE_TOKEN)
    .filter((part) => part.length > 0)
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`

      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={key}>{part.slice(2, -2)}</strong>
      }

      const markdownLink = part.match(MARKDOWN_LINK)
      if (markdownLink) {
        return renderLink(markdownLink[2], markdownLink[1], key)
      }

      if (BARE_URL.test(part)) {
        // A sentence-ending period is not part of the address.
        const trailing = part.match(TRAILING_PUNCTUATION)
        const href = trailing ? part.slice(0, -trailing[0].length) : part
        return (
          <React.Fragment key={key}>
            {renderLink(href, href, `${key}-a`)}
            {trailing ? trailing[0] : null}
          </React.Fragment>
        )
      }

      return <React.Fragment key={key}>{part}</React.Fragment>
    })

const renderTable = (lines: string[], keyPrefix: string): React.ReactNode => {
  const header = splitTableRow(lines[0])
  const rows = lines.slice(2).map(splitTableRow)

  return (
    <table className="web-chat-table" key={keyPrefix}>
      <thead>
        <tr>
          {header.map((cell, index) => (
            <th key={index}>
              {renderInline(cell, `${keyPrefix}-th-${index}`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex}>
                {renderInline(cell, `${keyPrefix}-td-${rowIndex}-${cellIndex}`)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const renderList = (lines: string[], keyPrefix: string): React.ReactNode => (
  <ul className="web-chat-list" key={keyPrefix}>
    {lines.map((line, index) => (
      <li key={index}>
        {renderInline(
          line.replace(/^\s*[-*]\s+/, ''),
          `${keyPrefix}-li-${index}`,
        )}
      </li>
    ))}
  </ul>
)

export const renderMarkdown = (markdown: string): React.ReactNode => {
  const blocks = markdown
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)

  return (
    <>
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').map((line) => line.trimEnd())
        const keyPrefix = `block-${blockIndex}`

        if (lines.length >= 2 && isSeparatorRow(lines[1])) {
          return renderTable(lines, keyPrefix)
        }

        if (lines.every(isBulletLine)) {
          return renderList(lines, keyPrefix)
        }

        return <p key={keyPrefix}>{renderInline(lines.join(' '), keyPrefix)}</p>
      })}
    </>
  )
}

// Splits a buffer on blank-line-delimited SSE frames, returning any frames
// that are complete and whatever partial text is left to buffer. A chunk
// from the stream reader can split a frame mid-way, so this must only ever
// parse complete `\n\n`-terminated frames.
//
// The server also sends a `: keepalive` comment frame every 15 seconds so
// Heroku's router doesn't close a long answer. A comment carries no `data:`
// line, so it filters down to nothing and is dropped here.
export const parseFrames = (
  buffer: string,
): { frames: StreamFrame[]; remainder: string } => {
  const frames: StreamFrame[] = []
  let rest = buffer
  let boundary = rest.indexOf('\n\n')

  while (boundary !== -1) {
    const rawFrame = rest.slice(0, boundary)
    rest = rest.slice(boundary + 2)

    const jsonText = rawFrame
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).replace(/^ /, ''))
      .join('\n')

    if (jsonText) {
      try {
        frames.push(JSON.parse(jsonText))
      } catch (e) {
        frames.push({
          type: 'error',
          message: 'Received a malformed response from the assistant.',
        })
      }
    }

    boundary = rest.indexOf('\n\n')
  }

  return { frames, remainder: rest }
}

// Rate limiting is handled before the JSON body is read. Rack::Attack's
// responder sends `error` as an object rather than a string, so the parsing
// below would miss it and fall through to the generic "unavailable" — which
// reads as a fault rather than a limit, and tells the user nothing about
// waiting.
const describeRetryAfter = (response: Response): string => {
  const reset = Number(response.headers.get('X-RateLimit-Reset'))
  if (!Number.isFinite(reset) || reset <= 0) {
    return 'You’ve asked a lot of questions in a short time. Please wait a moment and try again.'
  }

  const seconds = Math.max(1, Math.ceil(reset - Date.now() / 1000))
  const wait =
    seconds < 90
      ? `${seconds} second${seconds === 1 ? '' : 's'}`
      : `${Math.ceil(seconds / 60)} minutes`

  return `You’ve asked a lot of questions in a short time. You can ask again in about ${wait}.`
}

const describeFailure = async (response: Response): Promise<string> => {
  if (response.status === 429) return describeRetryAfter(response)

  const contentType = response.headers.get('content-type') || ''
  let parsedMessage: string | null = null

  if (contentType.includes('application/json')) {
    try {
      const body = await response.json()
      if (body && typeof body.error === 'string') parsedMessage = body.error
      else if (body && typeof body.message === 'string')
        parsedMessage = body.message
    } catch (e) {
      parsedMessage = null
    }
  }

  if (parsedMessage) return parsedMessage

  if (response.status === 401) {
    return 'Your session has expired. Please sign in again.'
  }
  if (response.status === 403) {
    return "You don't have access to the assistant."
  }
  return 'The assistant is unavailable right now. Please try again.'
}

const streamChat = async (
  message: string,
  onFrame: (frame: StreamFrame) => void,
  signal: AbortSignal,
): Promise<void> => {
  const token = localStorage.getItem(STORAGE_USER_TOKEN_KEY)

  const response = await fetch(CHAT_ENDPOINT, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ message }),
    signal,
  })

  if (!response.ok || !response.body) {
    onFrame({ type: 'error', message: await describeFailure(response) })
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()

    if (value) {
      buffer += decoder.decode(value, { stream: true })
      const { frames, remainder } = parseFrames(buffer)
      buffer = remainder
      frames.forEach(onFrame)
    }

    if (done) {
      buffer += decoder.decode()
      if (buffer.trim()) {
        const { frames } = parseFrames(`${buffer}\n\n`)
        frames.forEach(onFrame)
      }
      break
    }
  }
}

const FULL_SUGGESTIONS = [
  'what’s low in stock?',
  'what’s on my trucks?',
  'how much have we spent on orders this year?',
  'how are my cycle counts looking?',
  'who works at my branches?',
]

const DOCS_ONLY_SUGGESTIONS = [
  'how do I run a cycle count?',
  'how do checkouts work?',
  'how do I set a reorder threshold?',
  'what is a replenishment plan?',
]

const EXPANDED_STORAGE_KEY = 'foresight-assistant-expanded'
const LAUNCHER_TOP_STORAGE_KEY = 'foresight-assistant-launcher-top'
const LAUNCHER_SIZE = 56
const LAUNCHER_MARGIN = 20
// Below this, a pointer gesture is a click rather than a drag.
const DRAG_THRESHOLD_PX = 4

const clampLauncherTop = (top: number): number => {
  const highest = LAUNCHER_MARGIN
  const lowest = window.innerHeight - LAUNCHER_SIZE - LAUNCHER_MARGIN
  return Math.min(Math.max(top, highest), Math.max(highest, lowest))
}

const storedLauncherTop = (): number | null => {
  const raw = localStorage.getItem(LAUNCHER_TOP_STORAGE_KEY)
  if (raw === null) return null

  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

type AssistantMode = 'off' | 'full' | 'docs_only'

interface IConversation {
  mode: AssistantMode
  messages: Array<{ role: string; content: string }>
}

// Distinguishes "this export aged out" from "the download broke", so the panel
// can say the first without dressing it up as a failure.
class ExportExpired extends Error {
  constructor(message?: string) {
    super(
      message ||
        'That export is no longer available — exports are kept for 24 hours.',
    )
    this.name = 'ExportExpired'
  }
}

// Downloads are a fetch rather than a link because this app has no cookie
// session: a plain <a href> would arrive with no Authorization header and be
// refused. Fetching with the token and saving the blob keeps the credential in
// the header, where it belongs, and off the URL.
const requestExport = async (descriptor: IExport): Promise<void> => {
  const response = await fetch(EXPORT_ENDPOINT, {
    method: 'POST',
    // Every other request here omits credentials, and this one must too: the
    // endpoint's whole premise is that the bearer token says who is asking, and
    // Warden checks the session serializer before it runs any strategy. A stale
    // cookie would quietly build the file for whoever that session belongs to.
    credentials: 'omit',
    headers: authHeaders(),
    // Just the id now. The file was built during the turn and is served from
    // the row, so nothing here can change what it contains — which is what
    // retired the old whitelist of fields, where one dropped line turned a
    // bounded preview into an unbounded download.
    body: JSON.stringify({ id: descriptor.id }),
  })

  // 410 is the artifact having expired, which is ordinary rather than broken:
  // the message offering it lives in the conversation far longer than the
  // 24-hour TTL. The server sends wording meant for the manager, so prefer it.
  if (response.status === 410) {
    const body = await response.json().catch(() => ({}))
    throw new ExportExpired(body.error)
  }

  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`)
  }

  const blob = await response.blob()
  const objectUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = objectUrl
  link.download = descriptor.filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  // Deferred: the click starts the download asynchronously, and revoking in the
  // same tick cancels it outright in Safari and older Firefox.
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0)
}

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem(STORAGE_USER_TOKEN_KEY)}`,
})

// Asked once per page load. A failure returns 'off' so a broken or
// unreachable endpoint hides the panel rather than offering a chat that
// cannot answer.
const loadAvailability = async (): Promise<AssistantMode> => {
  try {
    const response = await fetch(AVAILABILITY_ENDPOINT, {
      credentials: 'omit',
      headers: authHeaders(),
    })
    if (!response.ok) return 'off'

    const body = (await response.json()) as { mode: AssistantMode }
    return body.mode || 'off'
  } catch (e) {
    return 'off'
  }
}

const loadConversation = async (): Promise<IConversation | null> => {
  try {
    const response = await fetch(CONVERSATION_ENDPOINT, {
      credentials: 'omit',
      headers: authHeaders(),
    })
    if (!response.ok) return null

    return (await response.json()) as IConversation
  } catch (e) {
    return null
  }
}

const resetConversation = async (): Promise<void> => {
  try {
    await fetch(RESET_ENDPOINT, {
      method: 'POST',
      credentials: 'omit',
      headers: authHeaders(),
    })
  } catch (e) {
    // The panel clears either way; a failed reset only means the server still
    // holds the old conversation, which the next load would surface.
  }
}

const WebChat: FunctionComponent = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(true)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [messages, setMessages] = useState<IChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [mode, setMode] = useState<AssistantMode | null>(null)
  const [availability, setAvailability] = useState<AssistantMode | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [hasRequestedExamples, setHasRequestedExamples] = useState(false)
  const [followups, setFollowups] = useState<string[]>([])
  const [downloading, setDownloading] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [isExpanded, setIsExpanded] = useState(
    () => localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true',
  )
  const [launcherTop, setLauncherTop] = useState<number | null>(
    storedLauncherTop,
  )

  const nextIdRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const dragRef = useRef<{
    startY: number
    startTop: number
    moved: boolean
  } | null>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, progress])

  // A position saved in a tall window can fall outside a short one, which
  // would strand the launcher off-screen with no way to reach it.
  useEffect(() => {
    const reclamp = () => {
      setLauncherTop((top) => (top === null ? null : clampLauncherTop(top)))
    }

    reclamp()
    window.addEventListener('resize', reclamp)

    return () => window.removeEventListener('resize', reclamp)
  }, [])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (isHidden) return undefined

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      // A half-typed question is worth more than tidiness: clicking into the
      // page to check something should not throw it away.
      if (inputValue) return

      if (panelRef.current && !panelRef.current.contains(target)) {
        dismiss()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss()
      }
    }

    // mousedown rather than click: selecting text inside the panel and
    // releasing outside it should not count as clicking away.
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isHidden, inputValue])

  // Asked once, on mount, before anything renders. Seeds mode too, so the
  // suggestions are right the first time the panel opens rather than after the
  // conversation arrives.
  useEffect(() => {
    let cancelled = false

    loadAvailability().then((available) => {
      if (cancelled) return

      setAvailability(available)
      if (available !== 'off') setMode(available)
    })

    return () => {
      cancelled = true
    }
  }, [])

  // The server replays the whole conversation to the model on every turn, so a
  // panel that opened blank would contradict what the assistant actually
  // remembers — and the user could not scroll back to an earlier answer.
  useEffect(() => {
    if (!isOpen || hasLoaded) return

    let cancelled = false

    loadConversation().then((conversation) => {
      if (cancelled || !conversation) {
        setHasLoaded(true)
        return
      }

      setMode(conversation.mode)
      setMessages(
        conversation.messages.map((message, index) => ({
          id: index + 1,
          role: message.role === 'assistant' ? 'assistant' : 'user',
          text: message.content,
        })),
      )
      nextIdRef.current = conversation.messages.length
      setHasLoaded(true)
    })

    return () => {
      cancelled = true
    }
  }, [isOpen, hasLoaded])

  // Buttons rather than a cycling placeholder. A placeholder shows one idea at
  // a time, disappears the moment the input is focused — exactly when someone
  // has decided to engage — and still leaves them to type the whole thing out.
  // Four shown together map what the assistant covers, data and how-to both,
  // and each is one click from an answer.
  const suggestions =
    mode === 'docs_only' ? DOCS_ONLY_SUGGESTIONS : FULL_SUGGESTIONS

  // Shown in full only on an empty conversation, where discovery is the whole
  // problem. Repeating them after an answer helped nobody: the same questions
  // reappear regardless of what was just asked, so they read as clutter rather
  // than a suggestion. Afterwards they collapse to one button that puts them
  // back — a way out for someone stuck, costing one line of space.
  const isNewConversation = messages.length === 0

  // Three states, in priority order. Follow-ups win when the model offered
  // any, because a question about the answer just given beats a generic
  // example. The static list covers the empty conversation, where there is no
  // answer to follow up on, and the request-examples button covers the case
  // where the model offered nothing and the user is stuck.
  const showExamples = isNewConversation || hasRequestedExamples
  const visibleSuggestions = showExamples
    ? suggestions.slice(0, 4)
    : followups.slice(0, 3)
  const isFollowupRow = !showExamples && visibleSuggestions.length > 0
  const showExamplesButton = !showExamples && visibleSuggestions.length === 0

  const placeholder = 'Ask a question…'

  // The panel mounts on first open and is only hidden after that, so the
  // open/close animation is driven by a class rather than by mounting. On that
  // first open the browser has never painted the panel collapsed, and with no
  // start value there is nothing to animate from — so it is painted collapsed
  // for one frame and released on the next.
  useEffect(() => {
    if (isHidden) {
      setIsCollapsed(true)
      return undefined
    }

    // Two frames, not one. A requestAnimationFrame callback runs before the
    // paint of the frame that scheduled it, so clearing the class in the first
    // callback means the collapsed state is never painted, there is no start
    // value, and the panel appears with no animation at all. The second frame
    // is the one that guarantees it was painted.
    let inner = 0
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        setIsCollapsed(false)
        // Focused here rather than on a timer from openChat: focus() is a
        // no-op inside a visibility:hidden subtree, which is exactly what the
        // panel is until this line runs.
        inputRef.current?.focus()
      })
    })

    return () => {
      window.cancelAnimationFrame(outer)
      window.cancelAnimationFrame(inner)
    }
  }, [isHidden])

  const openChat = () => {
    if (!isOpen) {
      setIsOpen(true)
    }
    setIsHidden(false)
  }

  // Vertical only: the launcher stays pinned to the right edge, and the user
  // slides it clear of whatever it is covering. The position is remembered.
  const handleLauncherPointerDown = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    const current =
      launcherTop ?? window.innerHeight - LAUNCHER_SIZE - LAUNCHER_MARGIN
    dragRef.current = { startY: event.clientY, startTop: current, moved: false }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleLauncherPointerMove = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    const drag = dragRef.current
    if (!drag) return

    const delta = event.clientY - drag.startY
    if (!drag.moved && Math.abs(delta) < DRAG_THRESHOLD_PX) return

    drag.moved = true
    setLauncherTop(clampLauncherTop(drag.startTop + delta))
  }

  const handleLauncherPointerUp = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    const drag = dragRef.current
    dragRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)

    if (!drag) return

    if (drag.moved) {
      // Persist only a real move, so a plain click never rewrites the stored
      // position with the value it already had.
      setLauncherTop((top) => {
        if (top !== null) {
          localStorage.setItem(LAUNCHER_TOP_STORAGE_KEY, String(top))
        }
        return top
      })
      return
    }

    openChat()
  }

  // Keyboard users cannot drag, so the same adjustment is available from the
  // arrow keys while the launcher has focus.
  const handleLauncherKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

    event.preventDefault()
    const current =
      launcherTop ?? window.innerHeight - LAUNCHER_SIZE - LAUNCHER_MARGIN
    const next = clampLauncherTop(
      current + (event.key === 'ArrowUp' ? -24 : 24),
    )
    setLauncherTop(next)
    localStorage.setItem(LAUNCHER_TOP_STORAGE_KEY, String(next))
  }

  // Hiding only. The conversation, a half-typed question, and an in-flight
  // turn all survive, so reopening is instant and nothing is lost.
  const toggleExpanded = () => {
    setIsExpanded((current) => {
      const next = !current
      localStorage.setItem(EXPANDED_STORAGE_KEY, String(next))
      return next
    })
  }

  const dismiss = () => {
    setIsHidden(true)
    window.setTimeout(() => launcherRef.current?.focus(), 0)
  }

  const handleCloseChat = () => {
    dismiss()
  }

  const addMessage = (
    role: ChatRole,
    text: string,
    tools?: string[],
    exports?: IExport[],
  ) => {
    nextIdRef.current += 1
    setMessages((prev) => [
      ...prev,
      { id: nextIdRef.current, role, text, tools, exports },
    ])
  }

  const handleDownload = async (descriptor: IExport) => {
    setDownloading(descriptor.filename)
    setDownloadError(null)

    try {
      await requestExport(descriptor)
    } catch (e) {
      // An expired artifact is not a failure and should not read like one —
      // the server's wording already says what happened and what to do.
      setDownloadError(
        e instanceof ExportExpired
          ? e.message
          : `${descriptor.filename} could not be downloaded. Please try again.`,
      )
    } finally {
      setDownloading(null)
    }
  }

  const handleStop = () => {
    abortRef.current?.abort()
    setProgress(null)
    setIsSending(false)
  }

  const handleReset = async () => {
    abortRef.current?.abort()
    await resetConversation()
    setMessages([])
    setProgress(null)
    setIsSending(false)
    setFollowups([])
    setHasRequestedExamples(false)
    inputRef.current?.focus()
  }

  const send = async (text?: string) => {
    const message = (text ?? inputValue).trim()
    if (!message || isSending) return

    addMessage('user', message)
    setInputValue('')
    // Collapse the examples however the question was asked — clicking one, or
    // typing after opening the list. The old follow-ups go too: they belong to
    // the previous answer, and if this turn fails they would otherwise sit
    // under an error message as though they still applied.
    setHasRequestedExamples(false)
    setFollowups([])
    setIsSending(true)
    setProgress('Thinking…')

    const controller = new AbortController()
    abortRef.current = controller

    // A turn is only finished if the server said so. The stream ending on its
    // own is not an answer: a dropped connection, a dyno restart mid-turn or
    // the router closing an idle stream all end the reader cleanly, and
    // without this the panel re-enabled the input and said nothing at all.
    let sawTerminalFrame = false

    try {
      await streamChat(
        message,
        (frame) => {
          switch (frame.type) {
            case 'status':
              setProgress(frame.text)
              break
            case 'tool':
              setProgress(frame.label)
              break
            case 'final':
              sawTerminalFrame = true
              setProgress(null)
              // The stream is the fresher source: it reflects the mode the
              // answer was actually produced under.
              if (frame.mode) {
                setMode(frame.mode)
                setHasLoaded(true)
              }
              addMessage('assistant', frame.reply, frame.tools, frame.exports)
              setFollowups(frame.followups || [])
              break
            case 'error':
              sawTerminalFrame = true
              setProgress(null)
              addMessage('error', frame.message)
              break
            default:
              break
          }
        },
        controller.signal,
      )

      // Stopping is the user's own doing and already handled by handleStop.
      if (!sawTerminalFrame && !controller.signal.aborted) {
        setProgress(null)
        addMessage(
          'error',
          'That answer did not come through — the connection closed before ' +
            'the assistant finished. Nothing was saved and nothing changed, ' +
            'so it is safe to ask the same question again. Broad requests are ' +
            'the usual cause: try one kind of data, fewer branches, or a ' +
            'shorter date range.',
        )
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        setProgress(null)
        addMessage(
          'error',
          'The assistant is unavailable right now. Please try again.',
        )
      }
    } finally {
      setIsSending(false)
      abortRef.current = null
    }
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    void send()
  }

  // Sends straight away rather than filling the input. Putting the text in the
  // box and waiting for a second click reintroduces exactly the friction the
  // button exists to remove.
  const handleSuggestion = (question: string) => {
    void send(question)
  }

  // Deliberately not a question to the assistant: the list is fixed and known,
  // so re-deriving it costs a round trip and seconds of waiting to produce
  // something less predictable than the list we already have.
  const handleShowExamples = () => {
    setHasRequestedExamples(true)
  }

  // Enter sends, Shift+Enter starts a new line — the convention people expect
  // from a chat box, and the reason this is a textarea rather than an input.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void send()
    }
  }

  // Nothing renders until the server has answered: a launcher that appeared and
  // then swapped identity would be worse than one that arrives a beat late.
  // After every hook, by the rules of hooks.
  if (availability === null) return null

  // 'off' is not "no chat" -- it is "not this chat". An environment that has
  // not opted in, and an account that has opted out, both land here, and both
  // should keep the support chat this panel displaced rather than lose chat
  // altogether.
  if (availability === 'off') return <SupportChat />

  return (
    <>
      {isHidden && (
        <button
          type="button"
          id="assistant-launcher"
          ref={launcherRef}
          className="web-chat-launcher"
          aria-label="Open ForeSight Intelligence - AI Assistant"
          title="Open ForeSight Intelligence - AI Assistant — drag to move it"
          onPointerDown={handleLauncherPointerDown}
          onPointerMove={handleLauncherPointerMove}
          onPointerUp={handleLauncherPointerUp}
          onKeyDown={handleLauncherKeyDown}
          style={
            launcherTop === null
              ? undefined
              : { top: launcherTop, bottom: 'auto' }
          }
        >
          <svg
            className="web-chat-svg"
            viewBox="-12 -12 48 48"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="11" cy="11" r="20" fill="#1063cd" />
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}
      {isOpen && (
        <section
          id="assistant-panel"
          ref={panelRef}
          className={`web-chat-container${isExpanded ? ' -expanded' : ''}${
            isCollapsed ? ' -collapsed' : ''
          }`}
          role="dialog"
          aria-label="ForeSight Intelligence - AI Assistant"
        >
          <header className="web-chat-header">
            <h2 className="web-chat-title">
              ForeSight Intelligence - AI Assistant
            </h2>
            <div className="web-chat-actions">
              <button
                type="button"
                id="assistant-expand"
                className="web-chat-action web-chat-icon-button"
                onClick={toggleExpanded}
                aria-pressed={isExpanded}
                aria-label={
                  isExpanded ? 'Shrink the assistant' : 'Expand the assistant'
                }
              >
                <svg
                  className="web-chat-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  {isExpanded ? (
                    <path d="M9 4v5H4M15 20v-5h5M20 9h-5V4M4 15h5v5" />
                  ) : (
                    <path d="M4 9V4h5M20 15v5h-5M15 4h5v5M9 20H4v-5" />
                  )}
                </svg>
              </button>
              <button
                type="button"
                id="assistant-reset"
                className="web-chat-action"
                onClick={handleReset}
                aria-label="Start a new conversation"
              >
                New chat
              </button>
              <button
                type="button"
                id="assistant-close"
                className="web-chat-action web-chat-close-button"
                onClick={handleCloseChat}
                aria-label="Close the assistant"
              >
                <svg
                  className="web-chat-close"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </header>
          {mode === 'docs_only' && (
            <p id="assistant-docs-only-notice" className="web-chat-notice">
              I can answer how-to and product questions. I can’t see your
              account data in this mode.
            </p>
          )}
          {hasLoaded && mode === null && (
            <p
              id="assistant-mode-unknown"
              className="web-chat-notice -warning"
              role="alert"
            >
              I couldn’t check what I have access to, so I may not be able to
              read your account data. Close and reopen the chat to try again.
            </p>
          )}
          <div className="web-chat-body">
            <div
              id="assistant-messages"
              className="web-chat-messages"
              role="log"
              aria-live="polite"
              aria-label="Conversation"
            >
              {messages.length === 0 && !progress && (
                <div id="assistant-empty" className="web-chat-empty">
                  Ask me anything about your account.
                </div>
              )}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`web-chat-message web-chat-message-${message.role}`}
                  data-role={message.role}
                >
                  {message.role === 'assistant' ? (
                    renderMarkdown(message.text)
                  ) : (
                    <p>{message.text}</p>
                  )}
                  {!!message.exports?.length && (
                    <div className="web-chat-exports">
                      {message.exports.map((file) => (
                        <button
                          key={file.filename}
                          type="button"
                          className="web-chat-export"
                          onClick={() => handleDownload(file)}
                          disabled={downloading === file.filename}
                        >
                          <svg
                            className="web-chat-icon"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path d="M12 3v12M7 10l5 5 5-5M4 20h16" />
                          </svg>
                          {downloading === file.filename
                            ? 'Preparing…'
                            : file.filename}
                        </button>
                      ))}
                    </div>
                  )}
                  {!!message.tools?.length && (
                    <div className="web-chat-message-tools">
                      {message.tools.join(' · ')}
                    </div>
                  )}
                </div>
              ))}
              {progress && (
                <div
                  id="assistant-progress"
                  className="web-chat-message web-chat-message-progress"
                >
                  {progress}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            {!isSending && visibleSuggestions.length > 0 && (
              <div
                id="assistant-suggestions"
                className="web-chat-suggestions"
                role="group"
                aria-label={
                  isFollowupRow
                    ? 'Suggested next questions'
                    : 'Example questions'
                }
              >
                {visibleSuggestions.map((question, index) => (
                  <button
                    key={question}
                    type="button"
                    id={`assistant-suggestion-${index}`}
                    className="web-chat-suggestion"
                    onClick={() => handleSuggestion(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}
            {!isSending && showExamplesButton && (
              <div className="web-chat-suggestions">
                <button
                  type="button"
                  id="assistant-show-examples"
                  className="web-chat-suggestion -muted"
                  onClick={handleShowExamples}
                >
                  What else can I ask?
                </button>
              </div>
            )}
            {downloadError && (
              <p className="web-chat-download-error" role="alert">
                {downloadError}
              </p>
            )}
            <form className="web-chat-form" onSubmit={handleSubmit}>
              <label
                className="web-chat-visually-hidden"
                htmlFor="assistant-input"
              >
                Ask the assistant a question
              </label>
              <textarea
                id="assistant-input"
                ref={inputRef}
                className="web-chat-input"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                rows={2}
                // Read-only rather than disabled: a disabled element loses
                // focus and does not get it back when re-enabled, so sending a
                // message dropped the caret to <body> and the next question
                // could not be typed without clicking back in. Read-only
                // refuses input without ever taking focus away. Enter is
                // already a no-op mid-turn — send() returns early while
                // isSending.
                readOnly={isSending}
              />
              {isSending ? (
                <button
                  type="button"
                  id="assistant-stop"
                  className="web-chat-send"
                  onClick={handleStop}
                >
                  Stop
                </button>
              ) : (
                <button
                  type="submit"
                  id="assistant-send"
                  className="web-chat-send"
                  disabled={!inputValue.trim()}
                >
                  Send
                </button>
              )}
            </form>
            <p className="web-chat-hint">
              Enter to send · Shift+Enter for a new line
            </p>
          </div>
        </section>
      )}
    </>
  )
}

export default WebChat
